import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useRBAC } from './RBACContext';
import { useAuth } from '../../state/auth-context';
import { useMfaAssurance } from '../../hooks/useMfaAssurance';
import {
  useMfaEnrollmentRequired,
  useMfaRequired,
} from '../../hooks/useTenantMfaSetting';
import { isMfaEnrollmentExemptPath } from '../../utils/tenantMfa';
import {
  canManagerAccessPath,
  isManagerRole,
} from '../feature-access';
import { UnauthorizedPage } from '../../components/UnauthorizedPage';
import type { UserRole } from './permissions';

interface RequireRoleProps {
  allowedRoles: UserRole[];
  children: React.ReactElement;
  fallbackPath?: string;
  loadingComponent?: React.ReactElement;
  /** When false, skip the MFA assurance / enrollment gates. */
  requireMfa?: boolean;
}

export const RequireRole: React.FC<RequireRoleProps> = ({
  allowedRoles,
  children,
  fallbackPath = '/signin',
  loadingComponent = <div>Loading...</div>,
  requireMfa = true,
}) => {
  const { hasAnyRole, loading: rbacLoading } = useRBAC();
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const { mfaRequired, isLoading: tenantMfaLoading, error: tenantMfaError } = useMfaRequired();
  const { isLoading: mfaLoading, needsChallenge, currentLevel } = useMfaAssurance();
  const { needsEnrollment, isLoading: enrollmentLoading } = useMfaEnrollmentRequired(
    requireMfa && mfaRequired,
  );

  if (authLoading || rbacLoading) {
    return loadingComponent;
  }

  if (!user) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Wait for the tenant MFA flag before allowing app access; resolving it after
  // first paint would let a page render behind the gate.
  if (requireMfa) {
    if (tenantMfaLoading) {
      return loadingComponent;
    }

    // If we cannot read the tenant flag, send the user to the MFA gate rather
    // than skipping MFA (fail closed for auth-sensitive config).
    if (tenantMfaError) {
      if (!isMfaEnrollmentExemptPath(location.pathname)) {
        return (
          <Navigate
            to="/mfa/verify"
            state={{ from: `${location.pathname}${location.search}` }}
            replace
          />
        );
      }
    } else if (mfaRequired) {
      if (mfaLoading || enrollmentLoading) {
        return loadingComponent;
      }

      // Session already MFA-verified — do not re-challenge or force enrollment.
      if (currentLevel !== 'aal2') {
        if (needsChallenge) {
          const fromPath = `${location.pathname}${location.search}`;
          return <Navigate to="/mfa/verify" state={{ from: fromPath }} replace />;
        }

        if (needsEnrollment && !isMfaEnrollmentExemptPath(location.pathname)) {
          return (
            <Navigate
              to="/mfa/verify"
              state={{ from: `${location.pathname}${location.search}`, enrollIfNeeded: true }}
              replace
            />
          );
        }
      }
    }
  }

  if (!hasAnyRole(allowedRoles)) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  return children;
};

const AUTHENTICATED_ROLES: UserRole[] = [
  'user',
  'admin',
  'super_admin',
  'employee',
  'manager',
];

const ManagerRouteGate: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { role } = useRBAC();
  const { pathname } = useLocation();

  if (isManagerRole(role) && !canManagerAccessPath(pathname)) {
    return <UnauthorizedPage />;
  }

  return children;
};

export const RequireAdmin: React.FC<Omit<RequireRoleProps, 'allowedRoles'>> = (props) => (
  <RequireRole {...props} allowedRoles={['admin', 'super_admin']} />
);

export const RequireSuperAdmin: React.FC<Omit<RequireRoleProps, 'allowedRoles'>> = (props) => (
  <RequireRole {...props} allowedRoles={['super_admin']} />
);

export const RequireUser: React.FC<Omit<RequireRoleProps, 'allowedRoles'>> = (props) => (
  <RequireRole {...props} allowedRoles={['user', 'admin', 'super_admin', 'employee']} />
);

export const RequireAnyRole: React.FC<Omit<RequireRoleProps, 'allowedRoles'>> = (props) => (
  <RequireRole {...props} allowedRoles={['user', 'admin', 'super_admin', 'employee']} />
);

export const RequireAuth: React.FC<Omit<RequireRoleProps, 'allowedRoles'>> = ({
  children,
  ...props
}) => (
  <RequireRole {...props} allowedRoles={AUTHENTICATED_ROLES}>
    <ManagerRouteGate>{children}</ManagerRouteGate>
  </RequireRole>
);

interface RedirectIfAuthenticatedProps {
  children: React.ReactElement;
  redirectPath?: string;
  loadingComponent?: React.ReactElement;
}

const DEFAULT_APP_ROLES: UserRole[] = AUTHENTICATED_ROLES;

export const RedirectIfAuthenticated: React.FC<RedirectIfAuthenticatedProps> = ({
  children,
  redirectPath = '/',
  loadingComponent = <div>Loading...</div>,
}) => {
  const { user, loading } = useAuth();
  const { hasAnyRole, loading: rbacLoading } = useRBAC();
  const { mfaRequired, isLoading: tenantMfaLoading, error: tenantMfaError } = useMfaRequired();
  const { isLoading: mfaLoading, needsChallenge, currentLevel } = useMfaAssurance();
  const { needsEnrollment, isLoading: enrollmentLoading } = useMfaEnrollmentRequired(mfaRequired);
  const location = useLocation();

  if (loading || rbacLoading) {
    return loadingComponent;
  }

  if (user && hasAnyRole(DEFAULT_APP_ROLES)) {
    if (tenantMfaLoading) {
      return loadingComponent;
    }
    if (tenantMfaError) {
      return <Navigate to="/mfa/verify" state={{ from: redirectPath }} replace />;
    }
    if (mfaRequired && (mfaLoading || enrollmentLoading)) {
      return loadingComponent;
    }
    if (mfaRequired && currentLevel !== 'aal2') {
      if (needsChallenge) {
        return <Navigate to="/mfa/verify" state={{ from: redirectPath }} replace />;
      }
      if (needsEnrollment) {
        return (
          <Navigate
            to="/mfa/verify"
            state={{ from: redirectPath, enrollIfNeeded: true }}
            replace
          />
        );
      }
    }
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  if (user && !hasAnyRole(DEFAULT_APP_ROLES)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};
